/**
 * AccountScreen.jsx
 * User account with REAL points, transactions, packages and invoices
 * With payment functionality
 */
import React, { useState } from 'react';
import { Wallet, Package, Plus, CreditCard, FileText, CheckCircle, Clock, AlertCircle, ChevronRight, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

// Get invoice status
const getInvoiceStatus = (status, t) => {
  switch (status) {
    case 'paid':
      return { icon: CheckCircle, bg: 'bg-green-500/20', text: 'text-green-400', label: t('account.status_paid') };
    case 'open':
    case 'pending':
      return { icon: Clock, bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: t('account.status_open') };
    case 'overdue':
      return { icon: AlertCircle, bg: 'bg-red-500/20', text: 'text-red-400', label: t('account.status_overdue') };
    case 'cancelled':
      return { icon: AlertCircle, bg: 'bg-zinc-700', text: 'text-zinc-400', label: t('account.status_cancelled') };
    default:
      return { icon: FileText, bg: 'bg-zinc-700', text: 'text-zinc-400', label: status };
  }
};

export const AccountScreen = ({ 
  points = 0, 
  creditBalance = 0, 
  transactions = [], 
  packages = [],
  invoices = [],
  subscriptions = [],
  onNavigate,
  onPayInvoice,
  onViewInvoice,
  initialTab = 'points'
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const { t } = useTranslation();
  
  // Filter invoices
  const openInvoices = invoices.filter(inv => inv.status === 'open' || inv.status === 'pending');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');
  
  // Active subscriptions
  const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');

  // Handle pay invoice
  const handlePayInvoice = async (invoice) => {
    if (onPayInvoice) {
      setPayingInvoice(invoice.id);
      try {
        await onPayInvoice(invoice);
      } finally {
        setPayingInvoice(null);
      }
    } else {
      // Show info that payment would open in customer portal
      toast.info(t('account.loading_text'));
    }
  };

  // Handle view invoice PDF
  const handleViewInvoice = (invoice) => {
    if (onViewInvoice) {
      onViewInvoice(invoice);
    } else {
      toast.info(t('account.loading_text'));
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 space-y-3 pb-20">
      <h1 className="text-white text-base font-bold pt-2">{t('account.title')}</h1>
      
      {/* Points & Credit Balance Summary */}
      <div className="bg-gradient-to-br from-yellow-500/30 to-yellow-600/10 rounded-xl p-4 border border-yellow-500/30">
        <div className="flex items-center gap-4">
          <div className="flex-1 text-center border-r border-yellow-500/30">
            <Wallet className="w-8 h-8 text-yellow-500 mx-auto mb-1" />
            <p className="text-white text-2xl font-bold">{points}</p>
            <p className="text-zinc-400 text-xs">{t('account.points_label')}</p>
          </div>
          <div className="flex-1 text-center">
            <CreditCard className="w-8 h-8 text-green-500 mx-auto mb-1" />
            <p className="text-white text-2xl font-bold">CHF {creditBalance.toFixed(2)}</p>
            <p className="text-zinc-400 text-xs">{t('account.credit_label')}</p>
          </div>
        </div>
      </div>

      {/* Active Package (if any) */}
      {packages.length > 0 && (
        <div className="bg-zinc-800/50 rounded-xl p-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-medium text-sm">{t('account.active_packages')}</span>
            <span className="text-yellow-400 text-xs">{t('account.active_count', { count: packages.length })}</span>
          </div>
          {packages.slice(0, 2).map((pkg, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-zinc-700/50 last:border-0">
              <Package className="w-4 h-4 text-yellow-400" />
              <div className="flex-1">
                <p className="text-white text-xs">{pkg.service_name || pkg.name}</p>
                <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden mt-1">
                  <div 
                    className="h-full bg-yellow-500 rounded-full" 
                    style={{ width: `${(pkg.remaining / pkg.total_units) * 100}%` }} 
                  />
                </div>
              </div>
              <span className="text-yellow-400 text-xs">{pkg.remaining}/{pkg.total_units}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        <button 
          onClick={() => setActiveTab('points')}
          className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${
            activeTab === 'points' ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          {t('account.tab_points')}
        </button>
        <button 
          onClick={() => setActiveTab('packages')}
          className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap ${
            activeTab === 'packages' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          {t('account.tab_packages')} ({packages.length})
        </button>
        <button 
          onClick={() => setActiveTab('subscriptions')}
          className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap ${
            activeTab === 'subscriptions' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          {t('account.tab_subscriptions')} ({activeSubscriptions.length})
        </button>
        <button 
          onClick={() => setActiveTab('invoices')}
          className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap flex items-center gap-1 ${
            activeTab === 'invoices' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          {t('account.tab_invoices')}
          {openInvoices.length > 0 && (
            <span className="w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
              {openInvoices.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('transactions')}
          className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap ${
            activeTab === 'transactions' ? 'bg-yellow-500 text-black font-medium' : 'bg-zinc-800 text-zinc-300'
          }`}
        >
          {t('account.tab_transactions')}
        </button>
      </div>

      {/* Points Tab */}
      {activeTab === 'points' && (
        <div className="bg-zinc-800/50 rounded-2xl p-4">
          <h2 className="text-white font-medium mb-3">{t('account.recent_transactions')}</h2>
          {transactions.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">{t('account.no_transactions')}</p>
          ) : (
            transactions.slice(0, 10).map((tx, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-zinc-700/50 last:border-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  tx.amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {tx.amount > 0 ? (
                    <Plus className="w-4 h-4 text-green-500" />
                  ) : (
                    <CreditCard className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm">{tx.description || tx.reason || t('account.transaction')}</p>
                  <p className="text-zinc-500 text-xs">{formatDate(tx.created_at)}</p>
                </div>
                <span className={`font-medium ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Packages Tab */}
      {activeTab === 'packages' && (
        <div className="space-y-2">
          {packages.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <Package className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('account.no_packages')}</p>
              <button 
                onClick={() => onNavigate && onNavigate('shop')}
                className="mt-3 px-4 py-2 bg-yellow-500 text-black text-sm rounded-lg font-medium"
              >
                {t('account.go_to_shop')}
              </button>
            </div>
          ) : (
            packages.map((pkg, i) => (
              <div key={i} className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{pkg.service_name || pkg.name}</p>
                    <p className="text-zinc-400 text-xs">{t('account.valid_until', { date: formatDate(pkg.valid_until) })}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-zinc-400">{t('account.remaining')}</span>
                    <span className="text-yellow-400">{t('account.remaining_of', { remaining: pkg.remaining, total: pkg.total_units })}</span>
                  </div>
                  <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500 rounded-full" 
                      style={{ width: `${(pkg.remaining / pkg.total_units) * 100}%` }} 
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-3">
          {/* Open Invoices */}
          {openInvoices.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
              <p className="text-red-400 text-xs font-medium mb-2">
                {t('account.open_invoices', { count: openInvoices.length })}
              </p>
              {openInvoices.map((inv) => {
                const status = getInvoiceStatus(inv.status, t);
                return (
                  <div key={inv.id} className="bg-zinc-900/50 rounded-lg p-3 mb-2 last:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${status.bg}`}>
                        <status.icon className={`w-4 h-4 ${status.text}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{inv.invoice_number}</p>
                        <p className="text-zinc-500 text-xs">{formatDate(inv.created_at || inv.date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">CHF {inv.total?.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleViewInvoice(inv)}
                        className="flex-1 py-2 bg-zinc-800 rounded-lg text-zinc-300 text-xs flex items-center justify-center gap-1"
                      >
                        <FileText className="w-3 h-3" />
                        PDF
                      </button>
                      <button 
                        onClick={() => handlePayInvoice(inv)}
                        disabled={payingInvoice === inv.id}
                        className="flex-1 py-2 bg-yellow-500 rounded-lg text-black text-xs font-medium flex items-center justify-center gap-1 disabled:opacity-50"
                      >
                        {payingInvoice === inv.id ? (
                          t('account.loading_text')
                        ) : (
                          <>
                            <ExternalLink className="w-3 h-3" />
                            {t('account.pay')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* All Invoices */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <h2 className="text-white font-medium mb-3">{t('account.all_invoices')}</h2>
            {invoices.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">{t('account.no_invoices')}</p>
            ) : (
              invoices.slice(0, 10).map((inv) => {
                const status = getInvoiceStatus(inv.status, t);
                return (
                  <button 
                    key={inv.id} 
                    onClick={() => handleViewInvoice(inv)}
                    className="w-full flex items-center gap-3 py-2 border-b border-zinc-700/50 last:border-0 text-left"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${status.bg}`}>
                      <status.icon className={`w-4 h-4 ${status.text}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm">{inv.invoice_number}</p>
                      <p className="text-zinc-500 text-xs">{formatDate(inv.created_at || inv.date)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">CHF {inv.total?.toFixed(2)}</p>
                      <span className={`text-xs ${status.text}`}>{status.label}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="space-y-2">
          {activeSubscriptions.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <CreditCard className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('account.no_subscriptions')}</p>
              <button 
                onClick={() => onNavigate && onNavigate('shop')}
                className="mt-3 px-4 py-2 bg-yellow-500 text-black text-sm rounded-lg font-medium"
              >
                {t('account.discover_subs')}
              </button>
            </div>
          ) : (
            subscriptions.map((sub, i) => {
              const isActive = sub.status === 'active';
              return (
                <div key={i} className="bg-zinc-800/50 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-green-500/20' : 'bg-zinc-700'}`}>
                      <CreditCard className={`w-5 h-5 ${isActive ? 'text-green-400' : 'text-zinc-400'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{sub.plan_name || sub.name || 'Abo'}</p>
                      <p className="text-zinc-500 text-xs">
                        {isActive ? `${t('account.active_since', { date: formatDate(sub.start_date) })}` : sub.status}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">CHF {sub.price?.toFixed(2) || '—'}</p>
                      <p className="text-zinc-500 text-xs">{sub.interval || t('account.monthly')}</p>
                    </div>
                  </div>
                  {sub.next_billing_date && (
                    <p className="text-zinc-500 text-xs mt-2 pt-2 border-t border-zinc-700/50">
                      {t('account.next_payment', { date: formatDate(sub.next_billing_date) })}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Transactions Tab - Full History */}
      {activeTab === 'transactions' && (
        <div className="bg-zinc-800/50 rounded-xl p-4">
          <h2 className="text-white font-medium mb-3">{t('account.all_transactions')}</h2>
          {transactions.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">{t('account.no_transactions')}</p>
          ) : (
            transactions.map((tx, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-zinc-700/50 last:border-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  tx.amount > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {tx.amount > 0 ? (
                    <Plus className="w-4 h-4 text-green-500" />
                  ) : (
                    <CreditCard className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm">{tx.description || tx.reason || t('account.transaction')}</p>
                  <p className="text-zinc-500 text-xs">{formatDate(tx.created_at)}</p>
                </div>
                <span className={`font-medium ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </span>
              </div>
            ))
          )}
          {/* Link to full TransactionHistoryScreen */}
          <button
            onClick={() => onNavigate && onNavigate('transaction-history')}
            className="w-full mt-3 py-2.5 bg-zinc-700/50 rounded-xl text-yellow-400 text-sm font-medium hover:bg-zinc-700 transition-colors"
            data-testid="view-all-transactions-btn"
          >
            {t('account.all_transactions')} →
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountScreen;
